import React, { useMemo, useState } from 'react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { SegmentedControl } from '../../../reusable';
import { money } from '../treasury/treasuryUi';

const METRIC_ITEMS = [
  { key: 'amount', label: '$' },
  { key: 'quantity', label: 'Galones' },
];

const formatQty = (value) => Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 1 });

const BudgetRawMaterialsOverview = ({ budget = null, monthAxis = [], ui, loading = false }) => {
  const [metric, setMetric] = useState('amount');
  const monthCount = Math.max(1, monthAxis.length || Number(budget?.month_count) || 12);
  const headers = (monthAxis || []).map((item) => item?.label || item?.short_label || `${item?.month || ''}/${item?.year || ''}`);
  const rows = useMemo(() => {
    return (budget?.product_costs || [])
      .map((item) => {
        const amountMonths = Array.from({ length: monthCount }, (_, index) => Number((item.months || [])[index] || 0));
        const unitCost = Number(item.unit_cost || 0);
        const quantityMonths = amountMonths.map((value) => (unitCost > 0 ? value / unitCost : 0));
        return {
          id: item.id,
          sku: item.product_sku || '',
          name: item.product_name || item.name || 'Producto',
          unitCost,
          supplier: item.supplier || '',
          paymentTerm: item.payment_term_label || '',
          amountMonths,
          quantityMonths,
          amountTotal: amountMonths.reduce((acc, value) => acc + value, 0),
          quantityTotal: quantityMonths.reduce((acc, value) => acc + value, 0),
        };
      })
      .sort((a, b) => b.amountTotal - a.amountTotal || a.name.localeCompare(b.name));
  }, [budget, monthCount]);
  const totals = useMemo(() => {
    const amountMonths = Array.from({ length: monthCount }, (_, index) =>
      rows.reduce((acc, row) => acc + Number(row.amountMonths[index] || 0), 0)
    );
    const quantityMonths = Array.from({ length: monthCount }, (_, index) =>
      rows.reduce((acc, row) => acc + Number(row.quantityMonths[index] || 0), 0)
    );
    return {
      amountMonths,
      quantityMonths,
      amountTotal: amountMonths.reduce((acc, value) => acc + value, 0),
      quantityTotal: quantityMonths.reduce((acc, value) => acc + value, 0),
    };
  }, [rows, monthCount]);

  const th = {
    textAlign: 'right',
    padding: '8px 6px',
    borderBottom: `1px solid ${ui.border}`,
    fontSize: 11,
    color: ui.muted,
    whiteSpace: 'nowrap',
    background: ui.header,
  };

  const cell = (row, index) =>
    metric === 'quantity' ? formatQty(row.quantityMonths[index]) : money(row.amountMonths[index]);
  const totalCell = (row) => (metric === 'quantity' ? formatQty(row.quantityTotal) : money(row.amountTotal));

  if (loading) {
    return <p style={{ margin: 0, padding: '24px 12px', fontSize: 13, color: ui.muted }}>Calculando…</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          background: ui.panel,
          border: `1px solid ${ui.border}`,
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '10px 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>Costo de materias primas</h3>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: ui.muted }}>
              Proviene de Costo de producto · {formatQty(totals.quantityTotal)} galones · {money(totals.amountTotal)}
            </p>
          </div>
          <SegmentedControl items={METRIC_ITEMS} activeKey={metric} onChange={setMetric} ariaLabel="Métrica de materias primas" />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 280 + headers.length * 88 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', minWidth: 220 }}>Producto</th>
                <th style={th}>Costo unit.</th>
                {headers.map((label, index) => (
                  <th key={`${label}-${index}`} style={th}>
                    {label || MONTH_LABELS[index]}
                  </th>
                ))}
                <th style={th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || row.sku}>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: ui.text }}>{row.name}</div>
                    <div style={{ fontSize: 11, color: ui.muted }}>
                      {[row.sku, row.supplier, row.paymentTerm].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: 12, color: ui.text }}>{money(row.unitCost)}</td>
                  {headers.map((_, index) => (
                    <td key={`${row.id}-m${index}`} style={{ padding: '8px 6px', textAlign: 'right', fontSize: 12, color: ui.text }}>
                      {cell(row, index)}
                    </td>
                  ))}
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: ui.text }}>
                    {totalCell(row)}
                  </td>
                </tr>
              ))}
              {rows.length ? (
                <tr>
                  <td style={{ padding: '8px 10px', fontSize: 12, fontWeight: 700, color: ui.text }} colSpan={2}>
                    Total
                  </td>
                  {headers.map((_, index) => (
                    <td key={`total-${index}`} style={{ padding: '8px 6px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: ui.text }}>
                      {metric === 'quantity' ? formatQty(totals.quantityMonths[index]) : money(totals.amountMonths[index])}
                    </td>
                  ))}
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: 12, fontWeight: 800, color: ui.text }}>
                    {metric === 'quantity' ? formatQty(totals.quantityTotal) : money(totals.amountTotal)}
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={headers.length + 3} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                    Aún no hay costos de producto. Agrégalos en Estructura de costos → Costo producto.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BudgetRawMaterialsOverview;
